import { useState, useEffect } from "react";
import { AltArrowRight } from "@solar-icons/react/ssr";
import { Switch, FormControlLabel } from "@mui/material";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import { Helmet } from "react-helmet";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";

import { request } from "@/Api";
import { selectIsBaseOrg, selectPersonId } from "@/auth/authSlice";
import { PERMISSION_CREATE_GLOBAL_BID_TEMPLATES } from "@/auth/constants";
import useEntitlements from "@/auth/useEntitlements";
import BidTemplatePopup from "@/bids/BidTemplatePopup";
import { InternalLink, PageTitle } from "@/common/CommonComponents";
import DeleteRowButton from "@/common/DeleteRowButton";
import { Heading, SectionTitle } from "@/common/typography";
import { setError } from "@/nav/navSlice";

const BidType = styled.div`
    margin-top: 48px;
`;
const Indented = styled.div`
    padding-left: 30px;
`;

export default function TemplateBids() {
    const dispatch = useDispatch();

    const [templates, setTemplates] = useState({});
    const [activePublicTemplateIds, setActivePublicTemplateIds] = useState([]);
    const [bidTemplatePopupOpen, setBidTemplatePopupOpen] = useState(false);

    const isBaseOrg = useSelector(selectIsBaseOrg);
    const personId = useSelector(selectPersonId);

    const { hasPermission } = useEntitlements();
    const canCreateGlobalTemplates = hasPermission(
        PERMISSION_CREATE_GLOBAL_BID_TEMPLATES,
    );

    const getTemplateBids = () => {
        request
            .get("/bid-templates", {
                params: {
                    set:
                        isBaseOrg || !canCreateGlobalTemplates
                            ? "active"
                            : "all",
                },
            })
            .then((response) => {
                const bid_templates_raw = response.data;
                setTemplates(
                    bid_templates_raw.reduce((acc, template) => {
                        const tab = template.public
                            ? "Public"
                            : template.person_id
                              ? "My Templates"
                              : "Workspace";
                        const category = template.category || "Custom";
                        acc[tab] = acc[tab] || {};
                        acc[tab][category] = acc[tab][category] || [];
                        acc[tab][category].push(template);
                        return acc;
                    }, {}),
                );
            });
    };

    const removeTemplateAndTemplateBid = (
        templateId,
        bidId,
        type,
        category,
    ) => {
        request.delete(`/bid-templates/${templateId}`).then(() => {
            request.delete(`/bids/${bidId}`).then(() => {
                const newTemplates = templates[type][category].filter(
                    (template) => {
                        return template.id != templateId;
                    },
                );
                setTemplates({
                    ...templates,
                    [type]: { ...templates[type], [category]: newTemplates },
                });
            });
        });
    };

    const togglePublicBidActive = (templateId, enabled) => () => {
        if (enabled) {
            setActivePublicTemplateIds(
                activePublicTemplateIds.filter((cur) => cur !== templateId),
            );
            request.delete(`/bid-templates/public/${templateId}`).catch(() => {
                dispatch(
                    setError({
                        message: "Failed to update active global templates",
                    }),
                );
            });
        } else {
            setActivePublicTemplateIds([
                ...activePublicTemplateIds,
                templateId,
            ]);
            request
                .post("/bid-templates/public", { bid_template_id: templateId })
                .catch(() => {
                    dispatch(
                        setError({
                            message: "Failed to update active global templates",
                        }),
                    );
                });
        }
    };

    useEffect(() => {
        getTemplateBids();

        if (canCreateGlobalTemplates) {
            request
                .get("/bid-templates", { params: { set: "active_public" } })
                .then((response) => {
                    setActivePublicTemplateIds(
                        response.data.map((template) => template.id),
                    );
                });
        }
    }, []);

    return (
        <>
            <Helmet>
                <PageTitle title="Bid Templates" />
            </Helmet>

            <Grid
                container
                justifyContent={"space-between"}
                alignItems={"center"}
                sx={{ marginBottom: "36px" }}
            >
                <Grid item>
                    <Heading
                        style={{ paddingTop: 12 }}
                    >{`${isBaseOrg ? "Global" : ""} Bid Templates`}</Heading>
                </Grid>
                <Grid item>
                    <Chip
                        clickable
                        onClick={() => {
                            setBidTemplatePopupOpen(true);
                        }}
                        label={"Create Template"}
                        variant={"outlined"}
                    />
                </Grid>
            </Grid>
            {Object.entries(templates).map((type) => {
                return (
                    <BidType key={type[0]}>
                        <SectionTitle>{type[0]}</SectionTitle>
                        {Object.entries(type[1]).map((category) => {
                            return (
                                <Indented key={category[0]}>
                                    <p>{category[0]}</p>
                                    {category[1].map((template) => (
                                        <TemplateBid
                                            key={template.id}
                                            template={template}
                                            type={type[0]}
                                            category={category[0]}
                                            isBaseOrg={isBaseOrg}
                                            removeTemplateAndTemplateBid={
                                                removeTemplateAndTemplateBid
                                            }
                                            personId={personId}
                                            canCreateGlobalTemplates={
                                                canCreateGlobalTemplates
                                            }
                                            enabled={activePublicTemplateIds.includes(
                                                template.id,
                                            )}
                                            togglePublicBidActive={
                                                togglePublicBidActive
                                            }
                                        />
                                    ))}
                                </Indented>
                            );
                        })}
                    </BidType>
                );
            })}

            <BidTemplatePopup
                bidTemplatePopupOpen={bidTemplatePopupOpen}
                setBidTemplatePopupOpen={setBidTemplatePopupOpen}
            />
        </>
    );
}

const TemplateBid = ({
    template,
    type,
    category,
    isBaseOrg,
    personId,
    canCreateGlobalTemplates,
    enabled,
    togglePublicBidActive,
    removeTemplateAndTemplateBid,
}) => {
    return (
        <Indented>
            <Grid
                container
                justifyContent={"space-between"}
                borderBottom={"1px solid grey"}
                alignItems={"center"}
                paddingTop={2}
                paddingBottom={2}
            >
                <Grid item>
                    <InternalLink
                        href={
                            template.public && !isBaseOrg
                                ? `/bids/public/${template.public_id}`
                                : `/bids/${template.bid_id}`
                        }
                        color="primary"
                    >
                        {template.name}
                    </InternalLink>
                </Grid>
                <Grid item>
                    <Grid
                        container
                        justifyContent={"end"}
                        alignItems="center"
                        gap={2}
                    >
                        {template.public &&
                        !isBaseOrg &&
                        canCreateGlobalTemplates ? (
                            <Grid item>
                                <FormControlLabel
                                    label="Enabled"
                                    control={
                                        <Switch
                                            checked={enabled}
                                            onChange={togglePublicBidActive(
                                                template.id,
                                                enabled,
                                            )}
                                        />
                                    }
                                />
                            </Grid>
                        ) : !template.public &&
                          (template.person_id == personId ||
                              canCreateGlobalTemplates) ? (
                            <Grid item>
                                <DeleteRowButton
                                    handleDeleteConfirm={() => {
                                        removeTemplateAndTemplateBid(
                                            template.id,
                                            template.bid_id,
                                            type,
                                            category,
                                        );
                                    }}
                                />
                            </Grid>
                        ) : null}
                        <Grid item>
                            <InternalLink
                                href={
                                    template.public && !isBaseOrg
                                        ? `/bids/public/${template.public_id}`
                                        : `/bids/${template.bid_id}`
                                }
                            >
                                <IconButton>
                                    <AltArrowRight />
                                </IconButton>
                            </InternalLink>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Indented>
    );
};
